

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ClearIcon from '@mui/icons-material/Clear';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import emlIcon from '../../assets/img/eml.png';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../redux/store/appStore';
import { getAiValidationHistory } from '../../redux/slices/historyLogs/aiValidationHistorySlice';
import api from '../../utils/api';
import { STATUS } from '../../utils/status';
import AiValidationHistoryDrawer from './AiValidationHistoryDrawer';
import {
  AiValidationHistoryRow,
  FilterOption,
  DEFAULT_IMAGE_INFO,
  PiiFlaggingData,
  BlurFlaggingData,
  DuplicateCheckData,
  DuplicateCheckEntry,
  ValidationSummary,
  safeTrim,
  normalizeStatusKey,
  formatRuntimeStatus,
  extractPreCheckResults,
} from './aiValidationHistoryTypes';

// ─── Local helpers ────────────────────────────────────────────────────────────

const toNumberOrNull = (value: unknown): number | null => {
  const parsedValue = Number(value);
  return Number.isNaN(parsedValue) ? null : parsedValue;
};

const formatAiValidationResponse = (responseValue: unknown): string => {
  if (responseValue === null || responseValue === undefined) return '-';
  if (typeof responseValue === 'string') return responseValue.trim() || '-';
  try {
    return JSON.stringify(responseValue, null, 2);
  } catch {
    return String(responseValue);
  }
};

const normalizeAssetTypeKey = (typeValue: string): string => {
  const normalizedValue = safeTrim(typeValue).toLowerCase();
  return normalizedValue.includes('email') ? 'eml' : typeValue;
};

const formatTypeLabel = (typeValue: string): string => {
  const normalizedValue = safeTrim(typeValue).toLowerCase();
  if (normalizedValue.includes('image')) return 'Image';
  if (normalizedValue.includes('eml') || normalizedValue.includes('email')) return 'EML';
  if (normalizedValue.includes('document') || normalizedValue.includes('doc')) return 'Document';
  if (normalizedValue.includes('audio')) return 'Audio';
  if (normalizedValue.includes('video')) return 'Video';
  if (normalizedValue.includes('text')) return 'Text';
  return typeValue || '-';
};

const getTypeBadgePrefix = (typeValue: string): string => {
  const normalizedValue = safeTrim(typeValue).toLowerCase();
  if (normalizedValue.includes('image')) return '🖼';
  if (normalizedValue.includes('eml') || normalizedValue.includes('email')) return '📧';
  if (normalizedValue.includes('audio')) return '🎵';
  if (normalizedValue.includes('video')) return '🎬';
  if (normalizedValue.includes('text')) return 'Image';
  if (normalizedValue.includes('document') || normalizedValue.includes('doc')) return '📄';
  return '';
};

const renderTypeBadgeContent = (typeValue: string) => {
  const normalizedValue = safeTrim(typeValue).toLowerCase();
  const label = formatTypeLabel(typeValue);

  if (normalizedValue.includes('eml') || normalizedValue.includes('email')) {
    return (
      <>
        <Box component='img' src={emlIcon} alt='EML' sx={{ width: 10, height: 10, mr: 0.45, objectFit: 'contain' }} />
        {label}
      </>
    );
  }

  return `${getTypeBadgePrefix(typeValue)} ${label}`.trim();
};

const getRuntimeStatusChipSx = (status: string) => {
  const normalizedStatus = normalizeStatusKey(status);
  if (normalizedStatus === 'pass') {
    return {
      color: '#16A34A',
      backgroundColor: '#EAFBF1',
      border: '1px solid #BBF7D0',
    };
  }

  if (normalizedStatus === 'warning') {
    return {
      color: '#B45309',
      backgroundColor: '#FFF7E6',
      border: '1px solid #FDE68A',
    };
  }

  return {
    color: '#DC2626',
    backgroundColor: '#FEF2F2',
    border: '1px solid #FECACA',
  };
};

const normalizeHistoryRow = (item: Record<string, unknown>, fallbackIndex: number): AiValidationHistoryRow => {
  const name = String(item.name ?? item.updated_by_name ?? item.file_name ?? '-');
  const type = normalizeAssetTypeKey(String(item.type ?? item.asset_type ?? item.validation_type ?? '-'));
  const runType = String(item.run_type ?? item.runType ?? '-');
  const assetType = normalizeAssetTypeKey(String(item.asset_type ?? item.type ?? '-'));
  const stage = String(item.stage_name ?? item.stage ?? item.stage_id ?? '-');
  const config = String(item.config ?? item.config_identifier ?? '-');
  const status = String(item.status ?? item.result_status ?? '-');
  const confidenceScore = String(item.confidence_percent ?? '-');
  const latency = String(item.latency ?? item.latency_ms ?? item.duration ?? '-');
  const promptTokens = Number(item.prompt_tokens ?? 0);
  const completionTokens = Number(item.completion_tokens ?? 0);
  const totalTokens = Number(item.total_tokens ?? item.tokens ?? item.token_count ?? 0);
  const date = String(item.date ?? item.created_at ?? item.timestamp ?? '-');
  const tokens = Number(item.tokens ?? item.token_count ?? 0);
  const cost = Number(item.cost ?? item.total_cost ?? item.estimated_cost ?? 0);
  const responseObj = item.response as Record<string, unknown> | null | undefined;
  const preChecksSource =
    responseObj?.pre_checks ??
    item.precheck_results ?? item.pre_check_results ?? item.pre_checks ??
    item.prechecks ?? item.pre_check_status ?? '-';
  const preCheckResults = extractPreCheckResults(preChecksSource);
  const preChecks = preCheckResults.length > 0
    ? preCheckResults.map((r) => r.title).join(', ')
    : String(preChecksSource ?? '-');
  const aiValidationResponse = formatAiValidationResponse(item.response);
  const imageInfo = item.image_info as Record<string, unknown> | undefined;

  // PII flagging
  const piiRaw = item.pii_flagging as Record<string, unknown> | null | undefined;
  const piiData: PiiFlaggingData | null =
    piiRaw !== null && piiRaw !== undefined && typeof piiRaw === 'object'
      ? {
          enabled: typeof piiRaw.enabled === 'boolean' ? piiRaw.enabled : false,
          isPiiDetected: typeof piiRaw.is_pii_detected === 'boolean' ? piiRaw.is_pii_detected : false,
          detectedTypes: Array.isArray(piiRaw.detected_types) ? (piiRaw.detected_types as string[]) : [],
          confidencePercent: toNumberOrNull(piiRaw.confidence_percent),
        }
      : null;

  // Duplicate check
  const dupRaw = item.duplicate_check as Record<string, unknown> | null | undefined;
  const extractDupEntry = (entryValue: unknown): DuplicateCheckEntry | null => {
    if (!entryValue || typeof entryValue !== 'object' || Array.isArray(entryValue)) return null;
    const entry = entryValue as Record<string, unknown>;
    return {
      method: String(entry.method ?? '-'),
      isDuplicate: typeof entry.is_duplicate === 'boolean' ? entry.is_duplicate : false,
      ...(typeof entry.enabled === 'boolean' ? { checkEnabled: entry.enabled } : {}),
      ...(typeof entry.advisory_only === 'boolean' ? { isAdvisory: entry.advisory_only } : {}),
    };
  };
  const hasDupData =
    dupRaw !== null &&
    dupRaw !== undefined &&
    typeof dupRaw === 'object' &&
    Object.keys(dupRaw).length > 0;
  const duplicateCheckData: DuplicateCheckData | null = hasDupData
    ? {
        exact: extractDupEntry(dupRaw!.exact),
        semantic: extractDupEntry(dupRaw!.semantic),
        similarity: extractDupEntry(dupRaw!.similarity),
        isDuplicate: typeof dupRaw!.is_duplicate === 'boolean' ? dupRaw!.is_duplicate : false,
      }
    : null;

  // Blur flagging from response.blur_flagging
  const blurFlagRaw = responseObj?.blur_flagging as Record<string, unknown> | null | undefined;
  const blurFlagDetails = String(blurFlagRaw?.details ?? '').trim().toLowerCase();
  const isBlurCheckNotRun = blurFlagDetails.includes('blur check did not run.');
  const blurFlaggingData: BlurFlaggingData | null =
    blurFlagRaw !== null &&
    blurFlagRaw !== undefined &&
    typeof blurFlagRaw === 'object' &&
    blurFlagRaw.enabled === true &&
    !isBlurCheckNotRun
      ? {
          enabled: true,
          passed: typeof blurFlagRaw.passed === 'boolean' ? blurFlagRaw.passed : false,
          isBlurDetected: typeof blurFlagRaw.is_blur_detected === 'boolean' ? blurFlagRaw.is_blur_detected : false,
          details: String(blurFlagRaw.details ?? '-'),
          confidencePercent: toNumberOrNull(blurFlagRaw.confidence_percent),
        }
      : null;

  // Combined pass count: pre-checks + PII (if enabled) + blur flagging (if enabled) + duplicate check (if present)
  const precheckPassCount: string = (() => {
    let passed = preCheckResults.filter(r => r.status === 'pass').length;
    let total = preCheckResults.length;
    if (piiData?.enabled) {
      total += 1;
      if (!piiData.isPiiDetected) passed += 1;
    }
    if (blurFlaggingData?.enabled) {
      total += 1;
      if (blurFlaggingData.passed) passed += 1;
    }
    if (duplicateCheckData) {
      total += 1;
      if (!duplicateCheckData.isDuplicate) passed += 1;
    }
    if (total === 0) return '0';
    return `${passed}/${total} pass`;
  })();

  // Blur check from request_payload
  const requestPayloadRaw = item.request_payload as Record<string, unknown> | null | undefined;
  const imageCheckPayload = requestPayloadRaw?.['image-check'] as Record<string, unknown> | undefined;
  const vendorRequestPayload = imageCheckPayload?.vendor_request as Record<string, unknown> | undefined;
  const controlsPayload = vendorRequestPayload?.controls as Record<string, unknown> | undefined;
  const assetChecksPayload = controlsPayload?.asset_checks as Record<string, unknown> | undefined;
  const imageAssetChecksPayload = assetChecksPayload?.image as Record<string, unknown> | undefined;
  const blurCheck: boolean | null =
    typeof imageAssetChecksPayload?.blur_check === 'boolean' ? imageAssetChecksPayload.blur_check : null;

  // Validation summary from root-level fields (also present in response.validation)
  const responseRaw = item.response as Record<string, unknown> | null | undefined;
  const responseValidation =
    responseRaw && typeof responseRaw === 'object'
      ? (responseRaw.validation as Record<string, unknown> | null | undefined)
      : null;
  const validationSummary: ValidationSummary | null =
    responseValidation && typeof responseValidation === 'object'
      ? {
          finalStatus: String(responseValidation.final_status ?? item.status ?? '-'),
          reasoning: String(responseValidation.reasoning ?? item.reasoning ?? '-'),
          failureReason:
            responseValidation.failure_reason != null
              ? String(responseValidation.failure_reason)
              : item.failure_reason != null
                ? String(item.failure_reason)
                : null,
          confidencePercent: toNumberOrNull(responseValidation.confidence_percent ?? item.confidence_percent),
        }
      : item.reasoning != null
        ? {
            finalStatus: String(item.status ?? '-'),
            reasoning: String(item.reasoning),
            failureReason: item.failure_reason != null ? String(item.failure_reason) : null,
            confidencePercent: toNumberOrNull(item.confidence_percent),
          }
        : null;

  return {
    id: String(item.id ?? `${name}-${fallbackIndex}`),
    name, type, runType, assetType, stage, config, status, confidenceScore, latency,
    promptTokens: Number.isNaN(promptTokens) ? 0 : promptTokens,
    completionTokens: Number.isNaN(completionTokens) ? 0 : completionTokens,
    totalTokens: Number.isNaN(totalTokens) ? 0 : totalTokens,
    date,
    tokens: Number.isNaN(tokens) ? 0 : tokens,
    cost: Number.isNaN(cost) ? 0 : cost,
    preChecks, precheckPassCount: precheckPassCount,
    preCheckResults, piiData, blurFlaggingData, duplicateCheckData, blurCheck, validationSummary, aiValidationResponse,
    imageInfo: {
      width: toNumberOrNull(imageInfo?.width),
      height: toNumberOrNull(imageInfo?.height),
      megapixels: toNumberOrNull(imageInfo?.megapixels),
      aspectRatio: toNumberOrNull(imageInfo?.aspect_ratio),
      mimeType: String(imageInfo?.mime_type ?? '-'),
      bits: toNumberOrNull(imageInfo?.bits),
      fileSize: toNumberOrNull(imageInfo?.file_size),
      isTrueColor: typeof imageInfo?.is_truecolor === 'boolean' ? imageInfo.is_truecolor : null,
    },
  };
};

// ─── Constants ────────────────────────────────────────────────────────────────

const AI_VALIDATION_HISTORY_HEADERS: string[] = [
  'Name', 'Type', 'Stage', 'Config', 'Status', 'Latency', 'Tokens', 'Cost', 'Pre-checks', 'Confidence %',
];

const STATUS_PLACEHOLDER = 'All Status';
const TYPE_PLACEHOLDER = 'All Types';
const RESULTS_PLACEHOLDER = 'Results';
const STAGE_PLACEHOLDER = 'All Stages';
const CONFIG_PLACEHOLDER = 'All Configs';
const STATUS_OPTIONS = ['pass', 'fail', 'warning'];
const RUN_TYPE_OPTIONS = ['LIVE', 'TEST'];
const TYPE_OPTIONS = ['image', 'document', 'text', 'audio', 'video', 'eml'];

// ─── Component ────────────────────────────────────────────────────────────────

const AiValidationHistory: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { id } = useParams();
  const associationType = useSelector((state: RootState) => state.projects.association_type);
  const aiValidationHistoryState = useSelector((state: RootState) => state.aiValidationHistory);

  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [allStatus, setAllStatus] = useState<string>('');
  const [allTypes, setAllTypes] = useState<string>('');
  const [allResults, setAllResults] = useState<string>('');
  const [allStages, setAllStages] = useState<string>('');
  const [allConfigs, setAllConfigs] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [startDateInputType, setStartDateInputType] = useState<'text' | 'date'>('text');
  const [endDateInputType, setEndDateInputType] = useState<'text' | 'date'>('text');
  const [searchText, setSearchText] = useState<string>('');
  const [debouncedSearchText, setDebouncedSearchText] = useState<string>('');
  const [refreshCounter, setRefreshCounter] = useState<number>(0);
  const [selectedRow, setSelectedRow] = useState<AiValidationHistoryRow | null>(null);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [drawerLoading, setDrawerLoading] = useState<boolean>(false);

  const rows = (aiValidationHistoryState.aiValidationHistory.results || []).map((item, index) =>
    normalizeHistoryRow(item, index)
  );
  const stageOptions = useMemo(() => {
    return Array.from(
      new Set(rows.map((row) => safeTrim(row.stage)).filter((value) => Boolean(value) && value !== '-'))
    ).map((value) => ({ id: value, label: value }));
  }, [rows]);
  const configOptions = useMemo(() => {
    const scopedRows = allStages
      ? rows.filter((row) => safeTrim(row.stage) === safeTrim(allStages))
      : rows;

    return Array.from(
      new Set(scopedRows.map((row) => safeTrim(row.config)).filter((value) => Boolean(value) && value !== '-'))
    ).map((value) => ({ id: value, label: value, stageId: allStages || undefined }));
  }, [rows, allStages]);
  const filteredRows = useMemo(() => {
    const queryValue = safeTrim(debouncedSearchText).toLowerCase();
    const stageQueryValue = safeTrim(allStages).toLowerCase();
    const configQueryValue = safeTrim(allConfigs).toLowerCase();
    const selectedTypeValue = safeTrim(allTypes).toLowerCase();
    const selectedResultValue = safeTrim(allResults).toUpperCase();

    return rows.filter((row) => {
      const rowName = safeTrim(row.name).toLowerCase();
      const rowType = safeTrim(row.type).toLowerCase();
      const rowRunType = safeTrim(row.runType).toUpperCase();
      const rowStage = safeTrim(row.stage).toLowerCase();
      const rowConfig = safeTrim(row.config).toLowerCase();
      const matchesSearch = !queryValue || rowName.includes(queryValue);
      const matchesType = !selectedTypeValue
        ? true
        : selectedTypeValue === 'eml'
          ? rowType === 'eml' || rowType === 'email'
          : rowType === selectedTypeValue;
      const matchesResults = !selectedResultValue || rowRunType === selectedResultValue;
      const matchesStatus = !allStatus || normalizeStatusKey(row.status) === normalizeStatusKey(allStatus);
      const matchesStage = !stageQueryValue || rowStage === stageQueryValue;
      const matchesConfig = !configQueryValue || rowConfig === configQueryValue;
      return matchesSearch && matchesType && matchesResults && matchesStatus && matchesStage && matchesConfig;
    });
  }, [rows, debouncedSearchText, allTypes, allResults, allStatus, allStages, allConfigs]);

  const summary = aiValidationHistoryState.aiValidationHistory.summary;
  const totalCount = aiValidationHistoryState.aiValidationHistory.count || 0;
  const isLoading = aiValidationHistoryState.getStatus === STATUS.PENDING;
  const errorMessage =
    aiValidationHistoryState.getStatus === STATUS.FAILED ? aiValidationHistoryState.error : null;
  const apiTypeFilterValue = safeTrim(allTypes).toLowerCase() === 'eml' ? 'email' : allTypes;

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearchText(safeTrim(searchText));
      setPage(0);
    }, 400);
    return () => clearTimeout(timeout);
  }, [searchText]);

  useEffect(() => {
    if (!allConfigs) return;
    if (!configOptions.some((option) => safeTrim(option.id) === safeTrim(allConfigs))) {
      setAllConfigs('');
      setPage(0);
    }
  }, [allConfigs, configOptions]);

  useEffect(() => {
    dispatch(
      getAiValidationHistory({
        ...(id ? { sub_project_id: id } : {}),
        page: page + 1, page_size: rowsPerPage,
        ...(associationType ? { association_type: associationType } : {}),
        ...(allStatus ? { status: allStatus } : {}),
        ...(allResults ? { run_type: safeTrim(allResults).toUpperCase() } : {}),
        ...(allTypes ? { type: apiTypeFilterValue } : {}),
        ...(allStages ? { stage: allStages } : {}),
        ...(allConfigs ? { config: allConfigs } : {}),
        ...(startDate ? { start_date: startDate } : {}),
        ...(endDate ? { end_date: endDate } : {}),
        ...(debouncedSearchText ? { search: debouncedSearchText } : {}),
      })
    );
  }, [
    dispatch,
    id,
    page,
    rowsPerPage,
    associationType,
    allStatus,
    allTypes,
    allResults,
    allStages,
    allConfigs,
    startDate,
    endDate,
    debouncedSearchText,
    refreshCounter,
  ]);

  const summaryCards = useMemo(() => [
    { title: 'Total Runs', value: summary?.total_runs ?? 0 },
    { title: 'Pass Rate', value: `${summary?.pass_rate ?? 0}%` },
    { title: 'Avg Latency (ms)', value: summary?.avg_latency_ms ?? 0 },
    { title: 'Total Tokens', value: summary?.total_tokens ?? 0 },
    { title: 'Estimated Cost', value: summary?.estimated_total_cost ?? 0 },
  ], [summary]);

  const handleChangeAllStatus = (event: SelectChangeEvent<string>) => { setAllStatus(event.target.value); setPage(0); };
  const handleChangeAllTypes = (event: SelectChangeEvent<string>) => { setAllTypes(event.target.value); setPage(0); };
  const handleChangeAllResults = (event: SelectChangeEvent<string>) => { setAllResults(event.target.value); setPage(0); };
  const handleChangeAllStages = (event: SelectChangeEvent<string>) => {
    const selectedStageId = event.target.value;
    setAllStages(selectedStageId);
    setAllConfigs('');
    setPage(0);
  };
  const handleChangeAllConfigs = (event: SelectChangeEvent<string>) => {
    const selectedConfigId = event.target.value;
    setAllConfigs(selectedConfigId);
    setPage(0);
  };
  const handleRefresh = () => setRefreshCounter((prev) => prev + 1);

  const handleRowClick = async (row: AiValidationHistoryRow) => {
    setDrawerOpen(true);
    setDrawerLoading(true);
    setSelectedRow({ ...row, imageInfo: row.imageInfo ?? DEFAULT_IMAGE_INFO });
    try {
      const queryString = new URLSearchParams({
        id: row.id,
        ...(id ? { sub_project_id: id } : {}),
        ...(associationType ? { association_type: associationType } : {}),
      }).toString();
      const response = await api.get(`/project/ai-validation/history/?${queryString}`);
      const responseData = response?.data;
      const candidateItem: Record<string, unknown> | null = Array.isArray(responseData?.data)
        ? (responseData.data[0] as Record<string, unknown> | undefined) ?? null
        : responseData?.data && typeof responseData.data === 'object'
          ? (responseData.data as Record<string, unknown>)
          : Array.isArray(responseData?.results)
            ? (responseData.results[0] as Record<string, unknown> | undefined) ?? null
            : responseData?.results && typeof responseData.results === 'object'
              ? (responseData.results as Record<string, unknown>)
              : responseData?.id != null
                ? (responseData as Record<string, unknown>)
                : null;
      if (candidateItem) setSelectedRow(normalizeHistoryRow(candidateItem, 0));
    } catch {
      setSelectedRow(row);
    } finally {
      setDrawerLoading(false);
    }
  };

  const handleDrawerClose = () => setDrawerOpen(false);

  return (
    <Box sx={{ width: '100%', minHeight: '100vh', backgroundColor: '#F3F5F9', p: { xs: 1.5, md: 2.5 }, boxSizing: 'border-box' }}>
      <Card sx={{ width: 'auto', minHeight: 'calc(100vh - 32px)', boxShadow: 'none', borderRadius: 3, display: 'flex', flexDirection: 'column', border: 'none', backgroundColor: '#F4F5F8', mx: { xs: 0.5, md: 1 } }}>
        <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: { xs: 1.25, md: 1.75 }, '&:last-child': { pb: { xs: 1.25, md: 1.75 } } }}>

          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
            <div>
              <Typography sx={{ fontFamily: 'Montserrat', fontWeight: 800, fontSize: { xs: '24px', md: '22px' }, lineHeight: 1.15, color: '#0F172A' }}>
                Validation History
              </Typography>
              <Typography sx={{ color: '#64748B', mt: 0.2, fontSize: '14px' }}>
                All saved validation runs — click any row to view full details.
              </Typography>
            </div>
            <Button
              variant='contained'
              onClick={handleRefresh}
              startIcon={<RefreshIcon />}
              sx={{ textTransform: 'none', fontFamily: 'Montserrat', fontWeight: 600, fontSize: '12px', minHeight: 38, borderRadius: 1.2, px: 1.8, py: 0.55, backgroundColor: '#2563EB', color: '#FFFFFF', boxShadow: 'none', '& .MuiButton-startIcon': { mr: 0.6, '& svg': { fontSize: '16px' } }, '&:hover': { backgroundColor: '#1D4ED8', boxShadow: 'none' } }}
            >
              Refresh
            </Button>
          </Box>

          {/* Summary cards */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(5, minmax(0, 1fr))' }, gap: 1.5, mb: 2.25 }}>
            {summaryCards.map((card) => (
              <Card key={card.title} sx={{ borderRadius: 2, boxShadow: '0px 2px 10px rgba(15, 23, 42, 0.06)', border: '1px solid #D7DEE8', backgroundColor: '#FFFFFF' }}>
                <CardContent sx={{ py: 1.2, '&:last-child': { pb: 1.2 } }}>
                  <Typography sx={{ fontFamily: 'Montserrat', fontSize: '10px', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#64748B' }}>{card.title}</Typography>
                  <Typography sx={{ fontFamily: 'Montserrat', fontWeight: 800, fontSize: '22px', lineHeight: '26px', color: card.title === 'Total Runs' ? '#1976d2' : card.title === 'Pass Rate' ? '#2e7d32' : '#111827' }}>
                    {card.value}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>

          {/* Filters */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center', overflowX: 'visible' }}>
            <FormControl size='small' sx={{ minWidth: 130, '& .MuiInputBase-root': { height: 38, borderRadius: 1.3, backgroundColor: '#FFFFFF', fontSize: '13px' }, '& .MuiSelect-select': { fontSize: '13px' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D0D8E3' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#BFC9D6' } }}>
              <Select value={allStatus} onChange={handleChangeAllStatus} displayEmpty
                renderValue={(v) => v ? v : <span style={{ color: '#9CA3AF' }}>{STATUS_PLACEHOLDER}</span>}>
                <MenuItem value=''><em>{STATUS_PLACEHOLDER}</em></MenuItem>
                {STATUS_OPTIONS.map((s) => <MenuItem key={s} value={s} sx={{ fontFamily: 'Montserrat', fontSize: '13px' }}>{s}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size='small' sx={{ minWidth: 130, '& .MuiInputBase-root': { height: 38, borderRadius: 1.3, backgroundColor: '#FFFFFF', fontSize: '13px' }, '& .MuiSelect-select': { fontSize: '13px' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D0D8E3' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#BFC9D6' } }}>
              <Select value={allTypes} onChange={handleChangeAllTypes} displayEmpty
                renderValue={(v) => v ? v : <span style={{ color: '#9CA3AF' }}>{TYPE_PLACEHOLDER}</span>}>
                <MenuItem value=''><em>{TYPE_PLACEHOLDER}</em></MenuItem>
                {TYPE_OPTIONS.map((t) => <MenuItem key={t} value={t} sx={{ fontFamily: 'Montserrat', fontSize: '13px' }}>{t}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size='small' sx={{ minWidth: 130, '& .MuiInputBase-root': { height: 38, borderRadius: 1.3, backgroundColor: '#FFFFFF', fontSize: '13px' }, '& .MuiSelect-select': { fontSize: '13px' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D0D8E3' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#BFC9D6' } }}>
              <Select value={allResults} onChange={handleChangeAllResults} displayEmpty
                renderValue={(v) => v ? v : <span style={{ color: '#9CA3AF' }}>{RESULTS_PLACEHOLDER}</span>}>
                <MenuItem value=''><em>{RESULTS_PLACEHOLDER}</em></MenuItem>
                {RUN_TYPE_OPTIONS.map((resultType) => (
                  <MenuItem key={resultType} value={resultType} sx={{ fontFamily: 'Montserrat', fontSize: '13px' }}>
                    {resultType}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size='small' sx={{ minWidth: 130, '& .MuiInputBase-root': { height: 38, borderRadius: 1.3, backgroundColor: '#FFFFFF', fontSize: '13px' }, '& .MuiSelect-select': { fontSize: '13px' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D0D8E3' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#BFC9D6' } }}>
              <Select value={allStages} onChange={handleChangeAllStages} displayEmpty
                renderValue={(v) => v ? stageOptions.find((o) => safeTrim(o.id) === safeTrim(v))?.label || v : <span style={{ color: '#9CA3AF' }}>{STAGE_PLACEHOLDER}</span>}>
                <MenuItem value=''><em>{STAGE_PLACEHOLDER}</em></MenuItem>
                {stageOptions.map((o) => <MenuItem key={o.id} value={o.id} sx={{ fontFamily: 'Montserrat', fontSize: '13px' }}>{o.label}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size='small' sx={{ minWidth: 130, '& .MuiInputBase-root': { height: 38, borderRadius: 1.3, backgroundColor: '#FFFFFF', fontSize: '13px' }, '& .MuiSelect-select': { fontSize: '13px' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D0D8E3' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#BFC9D6' } }}>
              <Select value={allConfigs} onChange={handleChangeAllConfigs} displayEmpty
                renderValue={(v) => v ? configOptions.find((o) => safeTrim(o.id) === safeTrim(v))?.label || v : <span style={{ color: '#9CA3AF' }}>{CONFIG_PLACEHOLDER}</span>}>
                <MenuItem value=''><em>{CONFIG_PLACEHOLDER}</em></MenuItem>
                {configOptions.map((o) => <MenuItem key={o.id} value={o.id} sx={{ fontFamily: 'Montserrat', fontSize: '13px' }}>{o.label}</MenuItem>)}
              </Select>
            </FormControl>
           
            <TextField
              size='small'
              type={startDateInputType}
              placeholder='Start date (dd/mm/yy)'
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setPage(0); }}
              onFocus={() => setStartDateInputType('date')}
              onBlur={() => setStartDateInputType(startDate ? 'date' : 'text')}
              InputProps={startDate ? {
                endAdornment: (
                  <InputAdornment position='end'>
                    <IconButton size='small' onClick={() => { setStartDate(''); setStartDateInputType('text'); setPage(0); }}>
                      <ClearIcon fontSize='small' />
                    </IconButton>
                  </InputAdornment>
                ),
              } : undefined}
              sx={{ minWidth: 150, '& .MuiInputBase-root': { height: 38, borderRadius: 1.3, backgroundColor: '#FFFFFF', fontSize: '13px' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D0D8E3' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#BFC9D6' }, '& .MuiInputBase-input::placeholder': { color: '#9CA3AF', opacity: 1, fontSize: '13px' } }}
            />
            <TextField
              size='small'
              type={endDateInputType}
              placeholder='End date (dd/mm/yy)'
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setPage(0); }}
              onFocus={() => setEndDateInputType('date')}
              onBlur={() => setEndDateInputType(endDate ? 'date' : 'text')}
              InputProps={endDate ? {
                endAdornment: (
                  <InputAdornment position='end'>
                    <IconButton size='small' onClick={() => { setEndDate(''); setEndDateInputType('text'); setPage(0); }}>
                      <ClearIcon fontSize='small' />
                    </IconButton>
                  </InputAdornment>
                ),
              } : undefined}
              sx={{ minWidth: 150, '& .MuiInputBase-root': { height: 38, borderRadius: 1.3, backgroundColor: '#FFFFFF', fontSize: '13px' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D0D8E3' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#BFC9D6' }, '& .MuiInputBase-input::placeholder': { color: '#9CA3AF', opacity: 1, fontSize: '13px' } }}
            />

             <TextField size='small' placeholder='Search by name...' value={searchText} onChange={(e) => setSearchText(e.target.value)}
              sx={{ minWidth: 150, '& .MuiInputBase-root': { height: 38, borderRadius: 1.3, backgroundColor: '#FFFFFF', fontSize: '13px' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D0D8E3' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#BFC9D6' }, '& .MuiInputBase-input::placeholder': { color: '#9CA3AF', opacity: 1, fontSize: '13px' } }} />
          </Box>

          {errorMessage ? <Alert severity='error' sx={{ mb: 3 }}>{errorMessage}</Alert> : null}

          {/* Table */}
          <TableContainer component={Paper} sx={{ borderRadius: 2, flex: 1, mt: 0.5, border: '1px solid #DDE3EA', boxShadow: 'none', overflow: 'hidden' }}>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  {AI_VALIDATION_HISTORY_HEADERS.map((header) => (
                    <TableCell key={header} sx={{ fontFamily: 'Montserrat', fontWeight: 700, fontSize: '12px', letterSpacing: '0.03em', textTransform: 'uppercase', color: '#5D6B7E', backgroundColor: '#F7F9FC', borderBottom: '1px solid #E3E8F0', py: 1.05 }}>
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={AI_VALIDATION_HISTORY_HEADERS.length} align='center'>
                      <Box sx={{ py: 2 }}><CircularProgress size={24} /></Box>
                    </TableCell>
                  </TableRow>
                ) : filteredRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={AI_VALIDATION_HISTORY_HEADERS.length} align='center'>
                      <Typography sx={{ fontFamily: 'Montserrat', py: 2 }}>No validation history found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRows.map((row) => (
                    <TableRow key={row.id} hover onClick={() => handleRowClick(row)} sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#FAFCFF' }, '& td': { borderBottom: '1px solid #E8EDF3', fontFamily: 'Montserrat', fontSize: '12px', py: 0.95, color: '#334155' } }}>
                      <TableCell>{row.name}</TableCell>
                      <TableCell>
                        <Box
                          component='span'
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            px: 1,
                            py: 0.2,
                            borderRadius: 999,
                            fontFamily: 'Montserrat',
                            fontSize: '10px',
                            fontWeight: 700,
                            color: '#2563EB',
                            backgroundColor: '#EFF6FF',
                            border: '1px solid #BFDBFE',
                          }}
                        >
                          {renderTypeBadgeContent(row.type)}
                        </Box>
                      </TableCell>
                      <TableCell>{row.stage}</TableCell>
                      <TableCell>{row.config}</TableCell>
                      <TableCell>
                        <Box component='span' sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', px: 1.05, py: 0.24, borderRadius: 999, fontFamily: 'Montserrat', fontSize: '10px', fontWeight: 700, letterSpacing: '0.03em', ...getRuntimeStatusChipSx(row.status) }}>
                          {`${normalizeStatusKey(row.status) === 'pass' ? '✓' : normalizeStatusKey(row.status) === 'fail' ? '✕' : '⚠'} ${formatRuntimeStatus(row.status)}`}
                        </Box>
                      </TableCell>
                      <TableCell>{safeTrim(row.latency).endsWith('ms') ? row.latency : `${row.latency}ms`}</TableCell>
                      <TableCell>{row.totalTokens}</TableCell>
                      <TableCell>{`$${row.cost.toFixed(5)}`}</TableCell>
                      <TableCell>{row.precheckPassCount}</TableCell>
                      <TableCell >{row.confidenceScore === '-' ? '-' : `${row.confidenceScore}%`}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            sx={{ mt: 1.2, '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontFamily: 'Montserrat', fontSize: '12px', color: '#5D6B7E' }, '& .MuiTablePagination-select': { fontSize: '12px' } }}
            component='div'
            count={totalCount}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </CardContent>
      </Card>

      <AiValidationHistoryDrawer
        open={drawerOpen}
        onClose={handleDrawerClose}
        loading={drawerLoading}
        selectedRow={selectedRow}
      />
    </Box>
  );
};

export default AiValidationHistory;